import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import InstructorLayout from "@/components/InstructorLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Plus, ChevronRight, Loader2, CheckCircle2, Clock, XCircle, PencilLine } from "lucide-react";

const STATUS_META = {
  draft: { label: "Borrador", cls: "bg-gray-500/20 text-gray-300", Icon: PencilLine },
  pending_review: { label: "En revisión", cls: "bg-amber-500/20 text-amber-300", Icon: Clock },
  approved: { label: "Aprobado", cls: "bg-emerald-500/20 text-emerald-300", Icon: CheckCircle2 },
  rejected: { label: "Rechazado", cls: "bg-red-500/20 text-red-300", Icon: XCircle },
};

export default function InstructorProgramas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [areas, setAreas] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/instructor/programs"),
        api.get("/catalog/areas"),
      ]);
      setItems(p.data.items || []);
      setAreas(a.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <InstructorLayout>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Instructor</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Mis programas</h1>
          <p className="mt-1 text-sm text-gray-400">Propón nuevos cursos. Serán revisados por el equipo editorial DCC antes de publicarse.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="instructor-program-create-btn"
                className="inline-flex items-center gap-2 rounded-md bg-[#F26A21] px-4 py-2 text-sm font-semibold text-[#0B0F14] hover:brightness-110">
          <Plus className="w-4 h-4" /> Nuevo programa
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="instructor-programs-empty">
          <BookOpen className="w-8 h-8 text-gray-500 mx-auto" />
          <p className="mt-3 text-sm text-gray-300">No has propuesto programas todavía.</p>
          <p className="mt-1 text-xs text-gray-500">Empieza por crear un borrador con la estructura básica y detalla módulos y lecciones desde el editor.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => {
            const meta = STATUS_META[p.review_status] || STATUS_META.draft;
            const Icon = meta.Icon;
            return (
              <Link key={p.id} to={`/app/instructor/programas/${p.id}/editor`}
                    data-testid={`instructor-program-row-${p.id}`}
                    className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08]">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-widest text-gray-500">{p.code} · {p.format}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{p.title}</p>
                  <p className="mt-1 text-xs text-gray-400 line-clamp-2">{p.description}</p>
                  {p.review_status === "rejected" && p.review_notes && (
                    <p className="mt-2 text-[11px] text-red-300">Notas: {p.review_notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${meta.cls}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateModal
          areas={areas}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); window.location.href = `/app/instructor/programas/${id}/editor`; }}
        />
      )}
    </InstructorLayout>
  );
}

function CreateModal({ areas, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", code: "", area_id: areas[0]?.id || "", format: "curso",
    duration_text: "8 horas", modality: "online", target_audience: "",
    description: "", syllabus_summary: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.code || !form.area_id) return;
    setSaving(true);
    try {
      const r = await api.post("/instructor/programs", form);
      toast.success("Borrador creado. Continúa en el editor.");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="instructor-program-create-modal"
            className="w-full max-w-2xl rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white">Nuevo programa</h2>
        <p className="mt-1 text-xs text-gray-400">Metadata inicial · en el editor definirás módulos y lecciones.</p>
        <div className="mt-5 grid gap-3">
          <Row label="Título">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} required
                   data-testid="program-title-input"
                   className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Código">
              <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} required
                     data-testid="program-code-input"
                     placeholder="Ej. F10-XYZ-01"
                     className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </Row>
            <Row label="Área">
              <select value={form.area_id} onChange={(e) => set("area_id", e.target.value)}
                      data-testid="program-area-select"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Row>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Row label="Formato">
              <select value={form.format} onChange={(e) => set("format", e.target.value)}
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                <option value="curso">Curso</option>
                <option value="diplomado">Diplomado</option>
                <option value="programa">Programa</option>
                <option value="seminario">Seminario</option>
                <option value="certificacion">Certificación</option>
              </select>
            </Row>
            <Row label="Modalidad">
              <select value={form.modality} onChange={(e) => set("modality", e.target.value)}
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                <option value="online">Online</option>
                <option value="presencial">Presencial</option>
                <option value="blended">Blended</option>
                <option value="mixto">Mixto</option>
              </select>
            </Row>
            <Row label="Duración">
              <input value={form.duration_text} onChange={(e) => set("duration_text", e.target.value)}
                     className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </Row>
          </div>
          <Row label="Audiencia objetivo">
            <input value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)}
                   className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
          </Row>
          <Row label="Descripción corta">
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
          </Row>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="program-create-submit"
                  className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crear borrador
          </button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-300 mb-1">{label}</span>
      {children}
    </label>
  );
}
